import { useEffect, useState } from 'react'
import {
  AskAiProvider,
  ASK_AI_DEFAULT_MODELS,
  ASK_AI_PROVIDER_LABELS,
  ASK_AI_PROVIDERS,
  AskAiProviderConfig,
  clearAskAiProvider,
  getAskAiGuide,
  hasAskAiKeyFor,
  loadAskAiConfig,
  loadAskAiConfigFor,
  saveAskAiConfig,
} from '../../services/aiProviderConfig'

interface AskAiConfigModalProps {
  open: boolean
  onClose: () => void
  onSaved?: (config: AskAiProviderConfig) => void
  /** Prefill provider when opening (e.g. user picked Gemini on chat bar). */
  initialProvider?: AskAiProvider | null
}

/**
 * Plain fixed overlay (not Headless Dialog) so it can sit on top of AskAiAnswerPanel
 * without nested-Dialog outside-click closing the parent.
 */
export default function AskAiConfigModal({
  open,
  onClose,
  onSaved,
  initialProvider,
}: AskAiConfigModalProps) {
  const [provider, setProvider] = useState<AskAiProvider>('gemini')
  const [apiKey, setApiKey] = useState('')
  const [model, setModel] = useState(ASK_AI_DEFAULT_MODELS.gemini)
  const [showKey, setShowKey] = useState(false)

  useEffect(() => {
    if (!open) return
    const existing = loadAskAiConfig()
    const nextProvider = initialProvider || existing?.provider || 'gemini'
    const forProvider = loadAskAiConfigFor(nextProvider)
    setProvider(nextProvider)
    setApiKey(forProvider?.apiKey || '')
    setModel(forProvider?.model || ASK_AI_DEFAULT_MODELS[nextProvider])
    setShowKey(false)
  }, [open, initialProvider])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const guide = getAskAiGuide(provider)

  const handleProviderChange = (next: AskAiProvider) => {
    setProvider(next)
    const saved = loadAskAiConfigFor(next)
    if (saved?.apiKey) {
      setApiKey(saved.apiKey)
      setModel(saved.model || ASK_AI_DEFAULT_MODELS[next])
      return
    }
    setApiKey('')
    setModel(ASK_AI_DEFAULT_MODELS[next])
  }

  const handleSave = () => {
    const trimmed = apiKey.trim()
    if (trimmed.length < 10) return
    const config: AskAiProviderConfig = {
      provider,
      apiKey: trimmed,
      model: model.trim() || ASK_AI_DEFAULT_MODELS[provider],
    }
    saveAskAiConfig(config)
    onSaved?.(config)
    onClose()
  }

  const handleClear = () => {
    // Chỉ xóa key của provider đang chọn — không đụng OpenAI khi đang xem Gemini, v.v.
    clearAskAiProvider(provider)
    setApiKey('')
    const stillActive = loadAskAiConfig()
    onSaved?.(
      stillActive || {
        provider,
        apiKey: '',
        model,
      }
    )
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ask-ai-config-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-m2-outline-variant bg-m2-surface-container p-5 shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 id="ask-ai-config-title" className="text-lg font-bold text-m2-on-surface">
          Cấu hình Ask AI
        </h2>
        <p className="mt-1 text-sm text-m2-on-surface-variant">
          Mỗi provider có API key riêng, lưu trên máy bạn (localStorage). Chọn tab OpenAI / Gemini /
          Claude để xem hoặc sửa key tương ứng.
        </p>

        <div className="mt-4 space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-m2-on-surface">
              Provider
            </label>
            <div className="flex flex-wrap gap-2">
              {ASK_AI_PROVIDERS.map((p) => {
                const saved = hasAskAiKeyFor(p)
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handleProviderChange(p)}
                    className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${
                      provider === p
                        ? 'border-m2-primary bg-m2-primary/10 text-m2-primary'
                        : 'border-m2-outline-variant text-m2-on-surface-variant hover:border-m2-primary/40'
                    }`}
                  >
                    {ASK_AI_PROVIDER_LABELS[p]}
                    {p === 'gemini' ? ' · Free' : ''}
                    {saved ? ' · ✓' : ''}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="rounded-xl border border-m2-outline-variant bg-m2-surface-low p-3">
            <p className="text-sm font-semibold text-m2-on-surface">{guide.title}</p>
            <ol className="mt-2 list-decimal space-y-1 pl-4 text-xs text-m2-on-surface-variant">
              {guide.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <a
              href={guide.url}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-block text-xs font-semibold text-m2-primary underline"
            >
              Mở trang lấy API key →
            </a>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-m2-on-surface">API key</label>
            <div className="flex gap-2">
              <input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="dán API key vào đây"
                autoComplete="off"
                className="min-w-0 flex-1 rounded-lg border border-m2-outline-variant bg-m2-surface-container px-3 py-2 text-sm text-m2-on-surface focus:border-m2-primary focus:outline-none focus:ring-1 focus:ring-m2-primary"
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                className="rounded-lg border border-m2-outline-variant px-3 text-xs font-medium text-m2-on-surface-variant"
              >
                {showKey ? 'Ẩn' : 'Hiện'}
              </button>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-m2-on-surface">
              Model (tuỳ chọn)
            </label>
            <input
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full rounded-lg border border-m2-outline-variant bg-m2-surface-container px-3 py-2 text-sm text-m2-on-surface focus:border-m2-primary focus:outline-none focus:ring-1 focus:ring-m2-primary"
            />
          </div>
        </div>

        <div className="mt-5 flex flex-wrap justify-between gap-2">
          <button
            type="button"
            onClick={handleClear}
            className="rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
          >
            Xóa key {ASK_AI_PROVIDER_LABELS[provider]}
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-m2-outline px-4 py-2 text-sm font-medium text-m2-on-surface-variant"
            >
              Đóng
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={apiKey.trim().length < 10}
              className="rounded-lg bg-m2-primary px-4 py-2 text-sm font-semibold text-m2-on-primary disabled:opacity-50"
            >
              Lưu
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
